import { useEffect, useCallback, useRef } from "react";

import { Socket } from 'socket.io-client';
import { ClientToServerEvents, ServerToClientEvents } from '../types/socket-events';
import { useSocket } from '../hooks/socket';

type C2SEvents = ClientToServerEvents;
type S2CEvents = ServerToClientEvents;

export function useWSListener<E extends keyof S2CEvents>(
  event: E,
  handler: S2CEvents[E]
) {
  const socket = useSocket(false) as Socket<S2CEvents, C2SEvents> | null;

  const stableHandler = useCallback(handler, []);

  useEffect(() => {
    if (!socket) return;
    
    socket.on(event as any, stableHandler as any);
    return () => {
      socket.off(event as any, stableHandler as any);
    };
  }, [socket, event, stableHandler]);
}

export function useCardPatch(
  flowId: number | undefined,
  cardId: number | undefined,
  onPatch: (updated: string[]) => void
) {
  const flowRef = useRef<number | undefined>(flowId);
  const cardRef = useRef<number | undefined>(cardId);
  const cbRef = useRef(onPatch);

  useEffect(() => {
    flowRef.current = flowId;
    cardRef.current = cardId;
    cbRef.current = onPatch;
  });

  const handler: ServerToClientEvents["CARD_PATCH"] = (data) => {
    if (
      flowRef.current === undefined ||
      cardRef.current === undefined
    )
      return;

    if (
      data.flowId === flowRef.current &&
      data.cardId === cardRef.current
    ) {
      cbRef.current(data.updated);
    }
  };

  useWSListener("CARD_PATCH", handler);
}